import { Button, Col, Form, Input, InputNumber, Row, Select, message } from "antd";
import CustomDatePicker from "common/CustomDatePicker";
import moment from "moment";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import {
  BOOKING_QTY_ADD_ENDPOINT,
  BOOKING_QTY_PUT_ENDPOINT,
  BUYER_INQUIRY_GET_COUNTRIES_ENDPOINT,
  GET_BUYER_ENDPOINT,
  GET_BUYING_OFFICE_ENDPOINT,
} from "../../../apiServices/API_ENDPOINTS";
import { getData, postData, putData } from "../../../apiServices/common";
import { isArrayAndHasValue } from "../../../utils/functions";

const PRODUCTION_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const BookingQtyForm = forwardRef(({ isEdit, onCloseModal, refetch, selectedItem }, ref) => {
  const [loading, setLoading] = useState(false);
  const [buyerLoading, setBuyerLoading] = useState(false);
  const [buyingOfficeLoading, setBuyingOfficeLoading] = useState(false);
  const [buyerList, setBuyerList] = useState([]);
  const [buyingOfficeList, setBuyingOfficeList] = useState([]);
  const [countryList, setCountryList] = useState([]);

  const [form] = Form.useForm();
  const { Option } = Select;
  const buyer_id_watch = Form.useWatch("buyer_id", form);
  const buyerIdValue = buyer_id_watch?.value ?? buyer_id_watch;
  const prevBuyerIdRef = useRef(null);
  const isLoadingSelectedRef = useRef(false);

  useImperativeHandle(ref, () => ({
    resetFields: () => form.resetFields(),
  }));

  useEffect(() => {
    getBuyerList({ per_page: 10 });
    getCountryList();
  }, []);

  useEffect(() => {
    if (buyerIdValue) {
      // Only clear buying_office when user changed buyer (not when loading selectedItem)
      const isUserChange = prevBuyerIdRef.current !== null && prevBuyerIdRef.current !== buyerIdValue;
      if (isUserChange && !isLoadingSelectedRef.current) {
        form.setFieldsValue({ buying_office_id: undefined });
      }
      prevBuyerIdRef.current = buyerIdValue;
      getBuyingOfficeList({ per_page: 10 });
    } else {
      prevBuyerIdRef.current = null;
      setBuyingOfficeList([]);
      form.setFieldsValue({ buying_office_id: undefined });
    }
  }, [buyerIdValue, form]);

  useEffect(() => {
    if (selectedItem) {
      isLoadingSelectedRef.current = true;
      // Ensure selected buyer and buying office are in lists before setting form
      if (selectedItem?.buyer?.id && selectedItem?.buyer?.name) {
        setBuyerList((prev) =>
          prev.some((b) => b.id === selectedItem.buyer.id)
            ? prev
            : [selectedItem.buyer, ...prev],
        );
      }
      if (selectedItem?.buyer_id) {
        getBuyingOfficeList({ per_page: 10 }).then(() => {
          // Ensure selected buying_office stays in list (API may not include it in first 10)
          if (selectedItem?.buying_office?.id && selectedItem?.buying_office?.name) {
            setBuyingOfficeList((prev) =>
              prev.some((bo) => bo.id === selectedItem.buying_office.id)
                ? prev
                : [selectedItem.buying_office, ...prev],
            );
          }
          form.setFieldsValue({
            id: selectedItem?.id,
            buyer_id: selectedItem?.buyer?.name
              ? { value: selectedItem.buyer_id, label: selectedItem.buyer.name }
              : selectedItem.buyer_id,
            buying_office_id: selectedItem?.buying_office?.name
              ? { value: selectedItem.buying_office_id, label: selectedItem.buying_office.name }
              : selectedItem.buying_office_id,
            country: selectedItem?.country,
            order_qty: selectedItem?.order_qty,
            production_month: selectedItem?.production_month,
            request_date: selectedItem?.request_date ? moment(selectedItem.request_date) : null,
            confirm_date: selectedItem?.confirm_date ? moment(selectedItem.confirm_date) : null,
            note: selectedItem?.note,
          });
          prevBuyerIdRef.current = selectedItem.buyer_id;
          isLoadingSelectedRef.current = false;
        });
      } else {
        form.setFieldsValue({
          id: selectedItem?.id,
          buyer_id: selectedItem?.buyer?.name
            ? { value: selectedItem.buyer_id, label: selectedItem.buyer.name }
            : selectedItem.buyer_id,
          buying_office_id: selectedItem?.buying_office?.name
            ? { value: selectedItem.buying_office_id, label: selectedItem.buying_office.name }
            : selectedItem.buying_office_id,
          country: selectedItem?.country,
          order_qty: selectedItem?.order_qty,
          production_month: selectedItem?.production_month,
          request_date: selectedItem?.request_date ? moment(selectedItem.request_date) : null,
          confirm_date: selectedItem?.confirm_date ? moment(selectedItem.confirm_date) : null,
          note: selectedItem?.note,
        });
        isLoadingSelectedRef.current = false;
      }
    }
  }, [selectedItem, form]);

  const getBuyerList = async (filterValues) => {
    setBuyerLoading(true);
    const bodyData = { per_page: 10, ...filterValues };
    const response = await getData(GET_BUYER_ENDPOINT, false, bodyData);
    if (response?.data?.code === 200) {
      setBuyerList(response?.data?.data?.data || []);
    }
    setBuyerLoading(false);
  };

  const getBuyingOfficeList = async (filterValues) => {
    setBuyingOfficeLoading(true);
    const bodyData = { per_page: 10, ...filterValues };
    const response = await getData(GET_BUYING_OFFICE_ENDPOINT, false, bodyData);
    if (response?.data?.code === 200) {
      setBuyingOfficeList(response?.data?.data?.data || []);
    }
    setBuyingOfficeLoading(false);
  };

  // Ensure selected buyer/buying_office are always in options for edit mode (show name not id)
  const buyerOptions = useMemo(() => {
    const list = [...(buyerList || [])];
    if (selectedItem?.buyer?.id && selectedItem?.buyer?.name && !list.some((b) => b.id === selectedItem.buyer.id)) {
      list.unshift(selectedItem.buyer);
    }
    return list;
  }, [buyerList, selectedItem?.buyer]);

  const buyingOfficeOptions = useMemo(() => {
    const list = [...(buyingOfficeList || [])];
    if (
      selectedItem?.buying_office?.id &&
      selectedItem?.buying_office?.name &&
      !list.some((bo) => bo.id === selectedItem.buying_office.id)
    ) {
      list.unshift(selectedItem.buying_office);
    }
    return list;
  }, [buyingOfficeList, selectedItem?.buying_office]);

  const getCountryList = async (filterValues) => {
    const bodyData = { per_page: 200, status: "active", ...filterValues };
    const response = await getData(BUYER_INQUIRY_GET_COUNTRIES_ENDPOINT, false, bodyData);
    if (response?.data?.code === 200) {
      setCountryList(response?.data?.data?.data || []);
    }
  };

  const getBuyerId = (v) => (v?.value != null ? v.value : v);
  const getBuyingOfficeId = (v) => (v?.value != null ? v.value : v);

  const createBookingQty = async (formValues) => {
    setLoading(true);
    const bodyData = {
      buyer_id: getBuyerId(formValues?.buyer_id),
      buying_office_id: getBuyingOfficeId(formValues?.buying_office_id),
      country: formValues?.country,
      order_qty: Number(formValues?.order_qty) || 0,
      production_month: formValues?.production_month,
      request_date: formValues?.request_date ? moment(formValues.request_date).format("YYYY-MM-DD") : null,
      confirm_date: formValues?.confirm_date ? moment(formValues.confirm_date).format("YYYY-MM-DD") : null,
      note: formValues?.note || null,
    };
    const response = await postData(BOOKING_QTY_ADD_ENDPOINT, bodyData);
    if (response?.code === 200) {
      setLoading(false);
      message.success("Booking Qty Created Successfully");
      refetch();
      form.resetFields();
      onCloseModal();
    } else {
      setLoading(false);
      message.error(response?.data?.message || "Something went wrong");
    }
  };

  const updateBookingQty = async (formValues) => {
    setLoading(true);
    const bodyData = {
      buyer_id: getBuyerId(formValues?.buyer_id),
      buying_office_id: getBuyingOfficeId(formValues?.buying_office_id),
      country: formValues?.country,
      order_qty: Number(formValues?.order_qty) || 0,
      production_month: formValues?.production_month,
      request_date: formValues?.request_date ? moment(formValues.request_date).format("YYYY-MM-DD") : null,
      confirm_date: formValues?.confirm_date ? moment(formValues.confirm_date).format("YYYY-MM-DD") : null,
      note: formValues?.note || null,
    };
    const response = await putData(BOOKING_QTY_PUT_ENDPOINT(formValues?.id), bodyData);
    if (response?.code === 200) {
      setLoading(false);
      message.success("Booking Qty Updated Successfully");
      refetch();
      form.resetFields();
      onCloseModal();
    } else {
      setLoading(false);
      message.error(response?.data?.message || "Something went wrong");
    }
  };

  return (
    <Form form={form} layout="vertical" onFinish={isEdit ? updateBookingQty : createBookingQty}>
      <Form.Item name="id" hidden />
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name="buyer_id" label="Buyer" rules={[{ required: true, message: "Buyer is required" }]}>
            <Select
              showSearch
              placeholder="Select Buyer"
              optionFilterProp="children"
              filterOption={false}
              size="small"
              allowClear
              loading={buyerLoading}
              labelInValue
              onSearch={(v) => getBuyerList({ name: v })}
              onClear={() => getBuyerList({ per_page: 10 })}
              onChange={(val) => form.setFieldsValue({ buyer_id: val || undefined, buying_office_id: undefined })}
            >
              {isArrayAndHasValue(buyerOptions) &&
                buyerOptions.map((item) => (
                  <Option value={item.id} key={item.id} label={item.name}>
                    {item.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="buying_office_id"
            label="Buying Office"
            rules={[{ required: true, message: "Buying Office is required" }]}
          >
            <Select
              showSearch
              placeholder="Select Buying Office"
              optionFilterProp="children"
              filterOption={false}
              size="small"
              allowClear
              loading={buyingOfficeLoading}
              disabled={!buyerIdValue}
              labelInValue
              onSearch={(v) => getBuyingOfficeList({ name: v })}
              onClear={() => buyerIdValue && getBuyingOfficeList({ per_page: 10 })}
            >
              {isArrayAndHasValue(buyingOfficeOptions) &&
                buyingOfficeOptions.map((item) => (
                  <Option value={item.id} key={item.id} label={item.name}>
                    {item.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="country" label="Country" rules={[{ required: true, message: "Country is required" }]}>
            <Select
              showSearch
              placeholder="Select Country"
              optionFilterProp="children"
              size="small"
              onSearch={(v) => getCountryList({ name: v })}
            >
              {isArrayAndHasValue(countryList) &&
                countryList.map((item) => (
                  <Option value={item.id} key={item.id}>
                    {item.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="order_qty" label="Order Qty" rules={[{ required: true, message: "Order Qty is required" }]}>
            <InputNumber placeholder="Order Qty" style={{ width: "100%" }} size="small" min={1} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="production_month"
            label="Production Month"
            rules={[{ required: true, message: "Production Month is required" }]}
          >
            <Select placeholder="Select Month" size="small">
              {PRODUCTION_MONTHS.map((m) => (
                <Option value={m} key={m}>
                  {m}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="request_date" label="Request Date">
            <CustomDatePicker size="small" style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="confirm_date" label="Confirm Date">
            <CustomDatePicker size="small" style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item name="note" label="Note">
            <Input.TextArea placeholder="Note" rows={2} size="small" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}>
          <Button
            type="default"
            block
            onClick={() => {
              form.resetFields();
              onCloseModal();
            }}
            size="small"
          >
            Discard
          </Button>
        </Col>
        <Col span={12}>
          <Button type="primary" htmlType="submit" block loading={loading} size="small">
            Save
          </Button>
        </Col>
      </Row>
    </Form>
  );
});

BookingQtyForm.displayName = "BookingQtyForm";

export default BookingQtyForm;
